import robustFetch from "@/utils/robustFetch";
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

interface VerificationStatus {
  authenticated: boolean;
  emailVerified: boolean;
  email?: string;
  requiresVerification: boolean;
}

interface EmailVerificationGuardProps {
  children: React.ReactNode;
}

export default function EmailVerificationGuard({
  children,
}: EmailVerificationGuardProps) {
  const [verificationStatus, setVerificationStatus] =
    useState<VerificationStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    checkVerificationStatus();
  }, [location.pathname]);

  const checkVerificationStatus = async () => {
    const token = localStorage.getItem("auth_token");

    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await robustFetch("/api/verification-status", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const status = await response.json();
        setVerificationStatus(status);

        // Redirect to verification page if needed
        if (status.authenticated && status.requiresVerification) {
          // Allow access to verification-related pages, subscription flows, home, and pricing
          // These paths don't require email verification
          const allowedPaths = [
            "/verify-email",
            "/login",
            "/signup",
            "/forgot-password",
            "/reset-password",
            "/subscription/cancel",
            "/subscription/success",
            "/subscription",
            "/pricing",
            "/contact",
            "/api-docs",
            "/formats",
            "/convert",
          ];

          // Check for exact match on "/" (homepage) or prefix match on other paths
          const isAllowedPath =
            location.pathname === "/" ||
            allowedPaths.some((path) => location.pathname.startsWith(path));

          if (!isAllowedPath) {
            navigate("/verify-email", { replace: true });
            return;
          }
        }
      } else {
        // Invalid token or user not found
        if (response.status === 401) {
          localStorage.removeItem("auth_token");
          localStorage.removeItem("user_data");
          // SECURITY FIX: Clear session on auth failure
          localStorage.removeItem("session_id");
          const newGuestSession = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          localStorage.setItem("session_id", newGuestSession);
        }
      }
    } catch (error) {
      console.error("Error checking verification status:", error);
    } finally {
      setLoading(false);
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 bg-blue-600 rounded-full animate-pulse"></div>
          <div
            className="w-4 h-4 bg-blue-600 rounded-full animate-pulse"
            style={{ animationDelay: "0.2s" }}
          ></div>
          <div
            className="w-4 h-4 bg-blue-600 rounded-full animate-pulse"
            style={{ animationDelay: "0.4s" }}
          ></div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
